import React, { useEffect, useState, useRef } from 'react';
import { XIcon, SendIcon, SparklesIcon, MusicIcon, SaveIcon, UserIcon, BotIcon } from 'lucide-react';
interface Track {
  id: string;
  title: string;
  artist: string;
  imageUrl: string;
  duration: string;
}
interface PlaylistData {
  mood: string;
  tracks: Track[];
}
interface Message {
  id: string;
  role: 'user' | 'ai';
  content: string;
  type: 'text' | 'playlist';
  playlistData?: PlaylistData;
}
interface MoodMixChatProps {
  isOpen: boolean;
  onClose: () => void;
  onSavePlaylist: (playlist: PlaylistData) => void;
}
const MOCK_TRACKS: Track[] = [{
  id: '1',
  title: 'Midnight City',
  artist: 'M83',
  imageUrl: 'https://i.scdn.co/image/ab67616d0000b273297b2c53224bd19162f526e3',
  duration: '4:03'
}, {
  id: '2',
  title: 'Starboy',
  artist: 'The Weeknd',
  imageUrl: 'https://i.scdn.co/image/ab67616d0000b2734718e28d24527d9c74636283',
  duration: '3:50'
}, {
  id: '3',
  title: 'Get Lucky',
  artist: 'Daft Punk',
  imageUrl: 'https://i.scdn.co/image/ab67616d0000b27322d0a73855d5972b0704e3e3',
  duration: '6:09'
}, {
  id: '4',
  title: 'Instant Crush',
  artist: 'Daft Punk ft. Julian Casablancas',
  imageUrl: 'https://i.scdn.co/image/ab67616d0000b27322d0a73855d5972b0704e3e3',
  duration: '5:37'
}];
export const MoodMixChat = ({
  isOpen,
  onClose,
  onSavePlaylist
}: MoodMixChatProps) => {
  const [messages, setMessages] = useState<Message[]>([]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  // Initialize chat when opened
  useEffect(() => {
    if (isOpen && messages.length === 0) {
      setMessages([{
        id: 'init',
        role: 'ai',
        content: "Hi! I'm your MoodMix AI. Tell me how you're feeling or what kind of vibe you're looking for, and I'll craft the perfect playlist for you.",
        type: 'text'
      }]);
    }
  }, [isOpen, messages.length]);
  // Auto-scroll to bottom
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({
      behavior: 'smooth'
    });
  }, [messages, isTyping]);
  const handleSendMessage = async () => {
    if (!inputValue.trim()) return;
    const userText = inputValue;
    setInputValue('');
    // Add user message
    const userMsg: Message = {
      id: Date.now().toString(),
      role: 'user',
      content: userText,
      type: 'text'
    };
    setMessages(prev => [...prev, userMsg]);
    setIsTyping(true);
    // Simulate AI delay
    setTimeout(() => {
      setIsTyping(false);
      // Generate a mock playlist response
      const aiMsg: Message = {
        id: (Date.now() + 1).toString(),
        role: 'ai',
        content: `I've put together a mix for "${userText}". It's got some ${userText.includes('chill') ? 'relaxed' : 'upbeat'} vibes. How does this look?`,
        type: 'playlist',
        playlistData: {
          mood: userText,
          tracks: MOCK_TRACKS // In a real app, this would be dynamic
        }
      };
      setMessages(prev => [...prev, aiMsg]);
    }, 2000);
  };
  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendMessage();
    }
  };
  if (!isOpen) return null;
  return <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/60 backdrop-blur-sm p-4" data-id="element-235">
      <div className="w-full max-w-2xl h-[80vh] bg-[#121212]/90 border border-white/10 rounded-2xl shadow-2xl flex flex-col overflow-hidden backdrop-blur-xl" data-id="element-236">
        {/* Header */}
        <div className="p-4 border-b border-white/10 flex items-center justify-between bg-white/5" data-id="element-237">
          <div className="flex items-center space-x-3" data-id="element-238">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-green-400 to-blue-500 flex items-center justify-center" data-id="element-239">
              <SparklesIcon className="w-5 h-5 text-white" data-id="element-240" />
            </div>
            <div data-id="element-241">
              <h2 className="font-bold text-white" data-id="element-242">MoodMix AI</h2>
              <p className="text-xs text-green-400 flex items-center" data-id="element-243">
                <span className="w-2 h-2 rounded-full bg-green-500 mr-2 animate-pulse" data-id="element-244" />
                Online
              </p>
            </div>
          </div>
          <button onClick={onClose} className="p-2 hover:bg-white/10 rounded-full transition-colors text-gray-400 hover:text-white" data-id="element-245">
            <XIcon className="w-6 h-6" data-id="element-246" />
          </button>
        </div>

        {/* Messages Area */}
        <div className="flex-1 overflow-y-auto p-6 space-y-6" data-id="element-247">
          {messages.map(msg => <div key={msg.id} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`} data-id="element-248">
              <div className={`max-w-[85%] ${msg.role === 'user' ? 'bg-green-600 text-white' : 'bg-[#282828] text-gray-100'} rounded-2xl p-4 shadow-lg`} data-id="element-249">
                {msg.type === 'text' ? <p className="leading-relaxed" data-id="element-250">{msg.content}</p> : <div className="space-y-4" data-id="element-251">
                    <p className="leading-relaxed" data-id="element-252">{msg.content}</p>
                    {msg.playlistData && <div className="bg-black/40 rounded-xl p-3 border border-white/5 mt-3" data-id="element-253">
                        <div className="flex items-center justify-between mb-3 px-1" data-id="element-254">
                          <div className="flex items-center text-xs text-gray-400 uppercase font-bold tracking-wider" data-id="element-255">
                            <MusicIcon className="w-3 h-3 mr-1" data-id="element-256" />
                            Generated Playlist
                          </div>
                        </div>
                        <div className="space-y-2 mb-4" data-id="element-257">
                          {msg.playlistData.tracks.map((track, idx) => <div key={track.id} className="flex items-center p-2 hover:bg-white/5 rounded-lg transition-colors group" data-id="element-258">
                              <span className="text-xs text-gray-500 w-5" data-id="element-259">
                                {idx + 1}
                              </span>
                              <img src={track.imageUrl} alt={track.title} className="w-10 h-10 rounded shadow-sm mr-3" data-id="element-260" />
                              <div className="flex-1 min-w-0" data-id="element-261">
                                <div className="text-sm font-medium truncate text-white" data-id="element-262">
                                  {track.title}
                                </div>
                                <div className="text-xs text-gray-400 truncate" data-id="element-263">
                                  {track.artist}
                                </div>
                              </div>
                              <div className="text-xs text-gray-500" data-id="element-264">
                                {track.duration}
                              </div>
                            </div>)}
                        </div>
                        <button onClick={() => msg.playlistData && onSavePlaylist(msg.playlistData)} className="w-full bg-green-500 hover:bg-green-400 text-black font-bold py-3 rounded-xl transition-all flex items-center justify-center transform hover:scale-[1.02]" data-id="element-265">
                          <SaveIcon className="w-4 h-4 mr-2" data-id="element-266" />
                          Save to Library
                        </button>
                      </div>}
                  </div>}
              </div>
            </div>)}

          {isTyping && <div className="flex justify-start" data-id="element-267">
              <div className="bg-[#282828] rounded-2xl p-4 shadow-lg flex items-center space-x-2" data-id="element-268">
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
              animationDelay: '0ms'
            }} data-id="element-269" />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
              animationDelay: '150ms'
            }} data-id="element-270" />
                <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{
              animationDelay: '300ms'
            }} data-id="element-271" />
              </div>
            </div>}
          <div ref={messagesEndRef} data-id="element-272" />
        </div>

        {/* Input Area */}
        <div className="p-4 bg-[#181818] border-t border-white/10" data-id="element-273">
          <div className="relative flex items-center" data-id="element-274">
            <input type="text" value={inputValue} onChange={e => setInputValue(e.target.value)} onKeyDown={handleKeyDown} placeholder="Describe your vibe (e.g., 'Rainy jazz cafe' or 'High energy workout')..." className="w-full bg-[#282828] text-white placeholder-gray-500 rounded-full py-4 pl-6 pr-14 focus:outline-none focus:ring-2 focus:ring-green-500/50 transition-all border border-transparent focus:border-green-500/30" autoFocus data-id="element-275" />
            <button onClick={handleSendMessage} disabled={!inputValue.trim() || isTyping} className={`absolute right-2 p-2 rounded-full transition-all ${inputValue.trim() && !isTyping ? 'bg-green-500 text-black hover:scale-105' : 'bg-gray-700 text-gray-500 cursor-not-allowed'}`} data-id="element-276">
              <SendIcon className="w-5 h-5" data-id="element-277" />
            </button>
          </div>
          <p className="text-center text-xs text-gray-500 mt-3" data-id="element-278">
            AI can make mistakes. Please check generated playlists.
          </p>
        </div>
      </div>
    </div>;
};